/**
 * Canonical page keys — aligned with primary app sidebar navigation (My Team → Page Permissions).
 * ids must match Router.app.tsx accessPermissions checks where routes are gated.
 */
export const TEAM_NAV_LABEL = 'My Team'

export function teamNavLabel(_inOrgContext: boolean): string {
  return TEAM_NAV_LABEL
}

export type TeamPageCategory = 'navigation'

export type TeamPageDefinition = {
  id: string
  label: string
  category: TeamPageCategory
  ownerOnly?: boolean
}

/** Legacy / mistaken ids → canonical id (when reading saved access_permission). */
export const TEAM_PAGE_ID_ALIASES: Record<string, string> = {
  'custom-report': 'reports',
  'custom-reports': 'reports',
}

/** Single group matching sidebar “Navigation”. */
export const TEAM_PAGE_CATEGORIES: { key: TeamPageCategory; title: string }[] = [
  { key: 'navigation', title: 'Navigation' },
]

/** Sidebar order: main app nav, then Account (My Team, Preferences, My Plan). */
export const CANONICAL_TEAM_PAGES: TeamPageDefinition[] = [
  { id: 'dashboard', label: 'Dashboard', category: 'navigation' },
  { id: 'traffic', label: 'Traffic', category: 'navigation' },
  { id: 'ai', label: 'AI Chat', category: 'navigation' },
  { id: 'reports', label: 'Reports', category: 'navigation' },
  { id: 'integrations', label: 'Integrations', category: 'navigation' },
  { id: 'inventory', label: 'Inventory', category: 'navigation' },
  { id: 'shipping', label: 'Shipping', category: 'navigation' },
  { id: 'shared_accounts', label: 'My Team', category: 'navigation' },
  { id: 'preferences', label: 'Preferences', category: 'navigation' },
  { id: 'plan', label: 'My Plan', category: 'navigation' },
]

const CANONICAL_IDS = new Set(CANONICAL_TEAM_PAGES.map((p) => p.id))

/** Missing/undefined key means allow (legacy); only explicit `checked: false` blocks. */
export function teamPageAccessAllowed(
  accessPermissions: Record<string, boolean> | undefined | null,
  key: string
): boolean {
  return accessPermissions?.[key] !== false
}

/**
 * Capability-aware shipping gate. Replaces direct
 * `teamPageAccessAllowed(accessPermissions, 'shipping')` checks for
 * code paths that need to discriminate between view, purchase, claims,
 * and settings.
 *
 * Backward-compat rules (must match `sharedComponents/canShip.ts`):
 *   - explicit deny on the legacy 'shipping' key denies ALL four
 *     capabilities (preserves the old "block shipping" toggle)
 *   - explicit deny on the specific 'shipping:<cap>' key denies just
 *     that capability
 *   - missing/undefined defaults to allow (legacy/grandfathered)
 *
 * Use the matching `SHIPPING_CAP_KEYS` constants below to avoid typos.
 *
 * See docs/shipping-audit/09-shipping-permission-split.md.
 */
export const SHIPPING_CAP_KEYS = {
  VIEW: 'shipping:view',
  PURCHASE: 'shipping:purchase',
  CLAIMS: 'shipping:claims',
  SETTINGS: 'shipping:settings',
} as const

export type ShippingCapKey =
  (typeof SHIPPING_CAP_KEYS)[keyof typeof SHIPPING_CAP_KEYS]

export function teamShippingCapabilityAllowed(
  accessPermissions: Record<string, boolean> | undefined | null,
  capability: ShippingCapKey
): boolean {
  if (!accessPermissions) return true
  if (accessPermissions['shipping'] === false) return false
  return accessPermissions[capability] !== false
}

export function resolveTeamPageId(savedId: string): string {
  if (!savedId) return savedId
  return TEAM_PAGE_ID_ALIASES[savedId] || savedId
}

/** PayHelm MCP tool rows */
export const MCP_TOOL_TYPE = 'mcp_tool'
/** Team-wide permission to disconnect/remove others’ integrations (everyone may still backfill). */
export const INTEGRATION_MANAGE_TYPE = 'integration_manage'
/** Single permission row id — replaces legacy per-integration rows */
export const INTEGRATION_MANAGE_ALL_ID = 'all'

export function isIntegrationManageRow(item: { type?: string } | null | undefined): boolean {
  return !!item && item.type === INTEGRATION_MANAGE_TYPE
}

export function isPagePermissionRow(item: { type?: string } | null | undefined): boolean {
  return !!item && item.type !== MCP_TOOL_TYPE && item.type !== INTEGRATION_MANAGE_TYPE
}

/**
 * Merge stored access_permission with canonical pages. Missing pages default to checked (legacy safe).
 * Multiple legacy rows that alias to the same id (e.g. reports + custom-reports) combine with AND.
 */
export function mergeAccessPermissionWithCanonical(
  accessPermission: Array<Record<string, unknown>> | null | undefined
): Array<Record<string, unknown>> {
  const list = Array.isArray(accessPermission) ? accessPermission : []
  const savedPages = list.filter(isPagePermissionRow)
  const toolRows = list.filter((p) => p && p.type === MCP_TOOL_TYPE)
  const integrationManageRows = list.filter(isIntegrationManageRow)

  const aggregateChecked = new Map<string, boolean>()
  const savedByCanonicalId = new Map<string, Record<string, unknown>>()

  for (const row of savedPages) {
    const rawId = String((row as { id?: string }).id || '')
    const canon = resolveTeamPageId(rawId)
    if (!CANONICAL_IDS.has(canon)) continue
    const rowOn = (row as { checked?: boolean }).checked !== false
    const prev = aggregateChecked.get(canon)
    aggregateChecked.set(canon, prev === undefined ? rowOn : prev && rowOn)
    if (!savedByCanonicalId.has(canon)) {
      savedByCanonicalId.set(canon, { ...row, id: canon })
    }
  }

  const mergedPages = CANONICAL_TEAM_PAGES.map((def) => {
    const saved = savedByCanonicalId.get(def.id)
    const combinedChecked = aggregateChecked.get(def.id)
    const defaultChecked = def.ownerOnly ? false : true
    if (saved !== undefined && combinedChecked !== undefined) {
      return {
        ...saved,
        id: def.id,
        label: def.label,
        checked: def.ownerOnly ? false : combinedChecked,
      }
    }
    return {
      id: def.id,
      label: def.label,
      checked: defaultChecked,
    }
  })

  return [...mergedPages, ...toolRows, ...integrationManageRows]
}

/** Raw rows granting permission to disconnect any team integration (not backfill). */
export function getIntegrationManageRows(
  accessPermission: Array<Record<string, unknown>> | null | undefined
): Array<Record<string, unknown>> {
  const list = Array.isArray(accessPermission) ? accessPermission : []
  return list.filter(isIntegrationManageRow)
}

/**
 * Shared users: `integration_manage` row id `all` with checked true allows disconnecting/removing any team connection.
 * Backfill on the Integrations page is not gated by this flag.
 * Legacy per-integration rows: any checked true counts as allowed until re-saved.
 */
export function canManageAllTeamIntegrations(
  accessPermission: Array<Record<string, unknown>> | null | undefined
): boolean {
  const rows = getIntegrationManageRows(accessPermission)
  const allRow = rows.find((r) => String((r as { id?: string }).id || '') === INTEGRATION_MANAGE_ALL_ID)
  if (allRow != null) return (allRow as { checked?: boolean }).checked !== false
  return rows.some((r) => {
    const id = String((r as { id?: string }).id || '')
    if (!id || id === INTEGRATION_MANAGE_ALL_ID) return false
    return (r as { checked?: boolean }).checked !== false
  })
}

/** My Team table: one slot — may disconnect any team integration on vs off (backfill unchanged). */
export function countMemberIntegrationManageAccess(
  accessPermission: Array<Record<string, unknown>> | null | undefined
): { enabled: number; total: number } {
  const on = canManageAllTeamIntegrations(accessPermission)
  return { enabled: on ? 1 : 0, total: 1 }
}

export function getPagePermissionRows(
  accessPermission: Array<Record<string, unknown>> | null | undefined
): Array<{ id: string; label?: string; checked?: boolean }> {
  const merged = mergeAccessPermissionWithCanonical(accessPermission)
  return merged.filter(isPagePermissionRow) as Array<{ id: string; label?: string; checked?: boolean }>
}

export function countMemberPageAccess(
  accessPermission: Array<Record<string, unknown>> | null | undefined
): { enabled: number; total: number } {
  const pages = getPagePermissionRows(accessPermission)
  const memberRelevant = CANONICAL_TEAM_PAGES.filter((p) => !p.ownerOnly)
  const total = memberRelevant.length
  const byId = new Map(pages.map((p) => [p.id, !!p.checked]))
  let enabled = 0
  for (const def of memberRelevant) {
    if (byId.get(def.id)) enabled += 1
  }
  return { enabled, total }
}
